import {Component, OnDestroy, OnInit, ViewChild, HostListener} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NavigationExtras, NavigationStart, Router, RoutesRecognized} from '@angular/router';
import { Account, Config, UserClaims, HttpRequest, HttpResponse } from '@mitel/cloudlink-sdk';
import { ClHeaderComponent, CompanySwitcherService, AppContainer, ClSideNavComponent, AlertData } from '@mitel/cloudlink-console-components';
import {AppUserClaimsService} from './core/services/app-user-claims.service';
import {environment} from '../environments/environment';
import {MenuService} from './core/services/menu.service';
import {combineLatest, Subject, Subscription} from 'rxjs';
import {AccountsService} from './core/services/accounts.service';
import {AppLauncherService, LauncherAppContainer} from './core/services/app-launcher.service';
import {MenuItem} from './core/interfaces/menu-item';
import {MenuState} from './core/constants/menu-state.enum';
import {UserRoles, asyncJobOwner} from './core/constants/constants';
import { AuthService } from './core/services/auth.service';
import { ChildCommsService } from './core/services/child-comms.service';
import { AppInfoService } from './core/services/app-info.service';
import { OrdersService } from './core/services/orders.service';
import { FeatureToggleService } from './core/services/feature-toggle.service';
import { PolicyToggleService } from './core/services/policy-toggle.service';
import { GWPIntegrationService } from './core/services/gwp-integration.service';
import { MiAccessService } from './core/services/miaccess.service';
import { UsersService } from './core/services/users.service';
import { BillingCommsService } from './core/services/billing-comms.service';
import { ExternalEventsHandlerService } from './core/services/external-events-handler.service';
import { AdminService } from './core/services/admin.service';
import { AppStateService } from './core/services/app-state.service';
import { SupportPortalService } from './core/services/support-portal.service';
import { Title } from '@angular/platform-browser';
import { RBACAuthGuardService } from './core/guards/rbac-auth-guard.service';
import { XLaunchService } from './core/services/x-launch.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from './shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from './shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { InformationDialogComponent } from './shared/components/dialogs/information-dialog/information-dialog.component';
import { InformationDialogs } from './shared/components/dialogs/information-dialog/information-dialogs.enum';
import { PwaService } from './core/services/pwa.service';
import { IntegCustomerAdminPortalService } from './core/services/integ-customer-admin-portal.service';
import { MiVBAccountSettingsService } from './core/services/mivb-account-settings.service';
import { AsyncJobsService } from './core/services/async-jobs.service';
import { FeatureFlags } from './core/interfaces/feature-flags';
import { WalkMeSupportService } from './core/services/walkme-support.service';
import { AsyncJobData } from './core/interfaces/async-job-data';
import { CloudLinkNotifyService } from './core/services/cloudlink-notify.service';
import { initLogger, Logging } from './modules/meet-logger/services/logging/logging.service';
import { version } from '../environments/version';
import { ReportIssueModalComponent } from './modules/report-problem/components/report-issue-modal/report-issue-modal.component';
import { MSTeamsSharedService } from './core/services/msteams-shared.service';
import { filter, takeUntil } from 'rxjs/operators';
import { AccountManagementService } from './modules/account-management/services/account-management.service';
import { FeatureFlagPolicyService } from './modules/feature-flags/services/feature-flag-policy.service';
import { ZappaAccountService } from './core/services/zappa-account.service';
import { IntegUnifyPhoneService } from './core/services/integ-unify-phone.service';

const logContext = 'AppComponent';



@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
	title = 'Accounts';
	claims: UserClaims;

	apps: AppContainer[];
  alerts: AlertData[] = [];
  enableAlertsCAP: boolean = false;
  enableAlertsUser: boolean = false;
	enableAlertsTenant: boolean = false;
	enableAlertFeature: boolean = false;
	accountTagChanged: Subscription;
	subscription: Subscription = new Subscription();
	menuItems: MenuItem[];
	isAccountSelected = false;
	enableAppLauncher = false;
	@ViewChild('sideNav', { static: true}) sideNav: ClSideNavComponent;
	selectedMenuItemId: string;
	cloud: string = environment.cloud || '';
	interactive: boolean = true;
	currentLanguage: string = 'en';
	languages: any[];
	showSideNav = true;
	DO_NOT_ROUTE = '/norouting';
	startupComplete: Promise<any>[] = [];
	invalidAccountLogin: boolean = false;
	/**
	 * True indicates that we have not, or that this is the 1st time we are running through the handleCompanies() method.
	 * So, if true, som init will be done.
	 */
	firstTime: boolean = true;
	skipRevert: boolean = false;
	clientDropMenu: any = undefined;
	sessionExpiredDialogShown: boolean = false;
	showUpdateBanner: boolean = false;
	private onDestroy$ = new Subject<void>();

	asyncJobTenantTypes: asyncJobOwner[] = [];

	constructor(
		private authService: AuthService,	// Always keep it at top because Auth service injecting has to be before all the services. 													
		private translateSvc: TranslateService,		// Because auth service is initializing auth for all the api in the sdk.
		private clHeader: ClHeaderComponent,
		private router: Router,
		private appUserClaimsSvc: AppUserClaimsService,
		private menuSvc: MenuService,
		private accService: AccountsService,
		private companySwitcherService: CompanySwitcherService,
		private appLauncherService: AppLauncherService,
		private childCommsService: ChildCommsService,
		private appInfoService: AppInfoService,
		private orderService: OrdersService,
		private gwpService: GWPIntegrationService,
		private featureToggleService: FeatureToggleService,
		private policyToggleService: PolicyToggleService,
		private miAccessService: MiAccessService,
		private billingComsService: BillingCommsService,
		private usersService: UsersService,
		private extEventsHandlerService: ExternalEventsHandlerService,
		private adminService: AdminService,
		private appStateService: AppStateService,
		private supportPortalService: SupportPortalService,
		private titleService: Title,
		private rbacAuthGuardService: RBACAuthGuardService,
		private xlaunchService: XLaunchService,
		public dialog: MatDialog,
		private pwaService: PwaService,
		private capIntegService: IntegCustomerAdminPortalService,
		private mivbAccountSettingsService: MiVBAccountSettingsService,
		private asyncJobsService: AsyncJobsService,
		private walkMeService: WalkMeSupportService,
		private clNotifyService: CloudLinkNotifyService,
		private msteamsSharedService: MSTeamsSharedService,
		private accountManagementService: AccountManagementService,
		private featureFlagPolicyService: FeatureFlagPolicyService,
		private zappaAcctService: ZappaAccountService,
		private integUnifyPhoneService: IntegUnifyPhoneService
	) {
		this.insertCloudlinkHttpWrapperHook();
		initLogger();
		this.setupTranslationService();
		// check if @mitel/token is there. If not then user just logged in so remove currentUrl from session
		if (!localStorage.getItem('@mitel/token')) {
			sessionStorage.removeItem('currentUrl');
			sessionStorage.removeItem('refreshed');
		}
	}

	/**
	 * Hook into CloudLink's Config.httpService request() method and wrap it with console.logs so 
	 * we can log network call url, statusCode & x-mitel-correlation-id
	 */
	insertCloudlinkHttpWrapperHook() {
		const oriRequestMethod = Config.httpService.request;
		// attach a wrapper method that logs results before passing on the origional response
		Config.httpService.request = (params: HttpRequest) => {
			const url = params.url;
			const method = params.method;
			const body = params.body;
			return oriRequestMethod.bind(Config.httpService)(params)
				.then((result: HttpResponse) => {
					console.log(`ClHttpWrapper.${method} ${result.statusCode} ${url} corrId=${params.headers?.['x-mitel-correlation-id'] ?? ''}`);
					return result;
				})
				.catch((error: HttpResponse) => {
					console.error(`ClHttpWrapper.${method} ${error.statusCode} ${url} corrId=${params.headers?.['x-mitel-correlation-id'] ?? ''}`, { body }, error);
					throw error;
				});
		}
	}


	// listening for the iframed child SPAs message to check for session validity
	// consider moving this section to possibility child-comms.service
	@HostListener('window:message', ['$event'])
	onMessage(event) {
		if (event && event.data === 'CL_CREATE_ACCOUNT') {
			console.log('iframe child said to create account', event);
			this.router.navigate(['accounts', {new: true}])
			.catch(err => console.error('Function: onMessage, err: ', err));
		} 
		else if (event && event.data === 'CL_CHECK_SESSION') {
			console.log('iframe child said to check session', event);
			this.clHeader.checkValidSessionStatus();
		}
		else if (event && event.data === 'CL_REDIRECT_LOGIN') {
			console.log('iframe child said to redirect to login', event);
			this.clHeader.redirectToLogin();
		}
		else if (event && event.data === 'CL_GET_LANGUAGE') {
			console.log('federated child asking for current language', event);
			this.childCommsService.sentChildrenCurrentLanguage();
		}
		else if (event && event.data && event.data.type === 'CL_ADD_ASYNC_JOB') {
			if (event.data.asyncJobData) {
				this.asyncJobsService.addAsyncJob(event.data.asyncJobData);
			}
		}
		else if (event && event.data === 'CL_GET_FEATURE_FLAGS') {
			console.log('federated child asking for current feature flags', event);
			let featureFlags = this.featureToggleService.getPackagedFlags();
			if ( featureFlags && featureFlags.length > 0 )
			this.childCommsService.sendChildFeatureFlags(featureFlags);
		}
		else if (event && event.data && event.data.type && event.data.type === 'CL_DONEINTEGRATION') {
			console.log('iframe child said its done integration', event);
			this.gwpService.handleMessageFromChild(event);	
		} 
		else if (event && event.data === 'CL_REDIRECT_ACCOUNT_DETAIL') {
			if (this.billingComsService.getAccountIdOfCurrentAccount()) {
				console.log("Iframe child said to go to account details");
				this.menuSvc.initialRoute = ['accounts', this.billingComsService.getAccountIdOfCurrentAccount()];
				this.menuSvc.isSwitcherProcessingRequired = true;
				this.companySwitcherService.setCurrentAccountById(this.billingComsService.getAccountIdOfCurrentAccount());
			}
		}
		else if (event && event.data && event.data.type && event.data.type === 'CL_NAVIGATION') 
		{
				console.log('iframe child said it wants to navigate', event);
				this.childCommsService.handleChildRequestToNavigate(event);
		} 
		else if(event && event.data && event.data.type && event.data.type === 'CL_UNSAVEDCHANGES')
		{
			let unsavedChanges = event.data.unsavedChanges? event.data.unsavedChanges : false;
			console.log('child said unsaved changes state is: ', unsavedChanges);
			this.appStateService.setBlockNavigation(unsavedChanges);
		}
	}

	// on every window focus, check to make sure the session is valid
	@HostListener('window:focus', ['$event'])
	async onFocus( event: any) {
		console.log('The window now has focus and check if the session is valid');
		this.clHeader.checkValidSessionStatus();
		// here we will detect if the token has changed and display a message
		await this.checkForClaimsChange();
	
	}

	@HostListener('window:beforeunload') beforeunload() {
		  sessionStorage.setItem('refreshed', 'true');
	}

	/* istanbul ignore next */ 
	// this is temporary and there is no need to have test coverage for it
	@HostListener('document:keydown.shift.control.q') 
	async toggleCCNextFeature() {
		if ( this.xlaunchService.isXLaunchedFromBoss() == false )
		{
			let account = undefined;
			if ( this.appUserClaimsSvc.isAssumedRole() )
				account = this.accService.getCurrentAccount();
			else
				account = await this.accService.getAccountById(this.claims.accountId)

			console.log("Going to toggle the CC Feature", account)
			await this.featureToggleService.toggleCCIntegrationFeature( account );
		}
	}

	/* istanbul ignore next */ 
	// this is temporary and there is no need to have test coverage for it
	@HostListener('document:keydown.shift.control.z') 
	async toggleDeveloperFeature() {
		let account = await this.accService.getAccountById(this.claims.accountId)
		console.log("Going to toggle the Developer Feature")
		await this.featureToggleService.toggleAPIFeature( account );
		this.menuSvc.setAPIDeveloperAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_DEVELOPER_API));
	}

	getHostname() {
		return window.location.hostname;
	}

	ngOnInit() {
		Logging.important(`[${logContext}] Starting... cl-accounts-web v${version.appVersion}-${version['commit']?.substring(0, 7)} ${version.buildDate} ${environment.cloud}`, version);
		this.checkForUpdates();

		const hostname = this.getHostname();
		console.log("hostname:", hostname);
		if(hostname.includes('supportportal')){
			this.clHeader.setClientId(environment.supportPortalClientId);
			this.title = "Support Portal"; //for toolbar
			this.titleService.setTitle(this.title); //for browser tab
			this.appStateService.setSupportPortalMode(true);
			this.menuSvc.setState(MenuState.GlobalAdmin);
		} else if(hostname.includes('featureflags')){
			this.clHeader.setClientId(environment.featureFlagsClientId);
			this.title = "Feature Flags"; //for toolbar
			this.titleService.setTitle(this.title); //for browser tab
			this.appStateService.setFeatureFlagsMode(true);
			this.menuSvc.setState(MenuState.FeatureFlagsAdmin);
		} else {
			this.clHeader.setClientId(environment.clientId);
			this.clNotifyService.initializeNotify();
			this.handleAppLauncher();
		}

	  this.subscription.add(this.menuSvc.menuStateChanged().subscribe(res => {
			if ( this.checkForInvalidLogin() == true )
				return;

			if ( this.xlaunchService.isXLaunchedFromBoss() == false )
				this.isAccountSelected = !!this.menuSvc.getMenuState();
			else 
				this.isAccountSelected = false;

			console.log("MENU STATE CHANGE state and isAccount Selected", this.menuSvc.getMenuState(), this.isAccountSelected);
			
			this.menuItems = res.state;
			this.translateMenus();
			console.log('Function: menuStateChanged, this.menuSvc.initialRoute: ', this.menuSvc.initialRoute);
			if (this.menuSvc.initialRoute.length > 0) {
				setTimeout(() => {
					this.router.navigate(this.menuSvc.initialRoute);
				});
			}
			setTimeout(() => {
				// This timeout is placed to make sure that managed-accounts and billing page 
				// are opened up with correct menu item selected on refresh. Menu items for those 
				// pages are added later on after policy checks and correct menu item in 
				// side nav is selected when it receives NavigationEnd routing event. In order to make 
				// sure that correct menu item is selected, we need to make the route guard wait till menu items in side nav are updated.
				// Right now we don't have any means to know from side nav that menu items are updated.
				// This workaround will make sure that it gives ample time for sidenav to reflect the updated this.menuItems
				// object in it. That way navigation can proceed and at the end it will emit the NavigationEnd event.
				// Which in turns triggers the process for selecting correct menuItem for the pages in the sidenav.
				this.menuSvc.menuItemsUpdatedInSideNav.next([...this.menuItems]);
			});
		}));

		this.subscription.add( this.router.events.subscribe( async events => {
			if (events instanceof NavigationStart) {
				console.log(`${logContext} router NavigationStart event: ${events.url}`); // so we can see the users path through the app in problem reports

				this.adminService.flushAccountTagsCache();

				// is everything initialized that is needed before navigating?
				Promise.all(this.startupComplete).then( async result => {
					if ( this.extEventsHandlerService.urlProcessComplete.value == false ){
						if(events.url === '/' || events.url.startsWith('/dashboard') || events.url.startsWith('/accounts') || events.url === '/roles' 
							|| events.url === '/orders' || events.url === '/managed-accounts' || events.url === '/developer')
							sessionStorage.setItem('currentUrl', events.url);
						return;
					}
					sessionStorage.setItem('currentUrl', events.url);
					this.showSideNav = events.url != '/navigationError';
					if (this.claims && this.claims.role === UserRoles.ACCOUNT_ADMIN && (events.url === '' || events.url === '/') && !sessionStorage.getItem('refreshed')) {
						this.menuSvc.initialRoute = ['dashboard', this.claims.accountId];
						this.menuSvc.setState(MenuState.AccountAdmin, this.claims.accountId);
					} else if (this.claims.role === UserRoles.GLOBAL_ADMIN) {
						/* 
						To be developed.
						Putting here now to prevent the else if below 
						*/
						console.log("Routing for Global_Admin: ", events.url);
					} else if ((events.url === '/dashboard' || events.url === '/accounts' || events.url === '/') && 
									this.claims && this.claims.role != UserRoles.ACCOUNT_ADMIN && !sessionStorage.getItem('refreshed')) {
						// Following "if" code is used to make sure that when user is assumed role and hits back button then user lands on accounts list page.
						// We might have to remove it depending upon what decision is made for back and forward button issue #677
						const appLauncherAccount = this.appLauncherService.getAccount();
						if (this.menuSvc.getMenuState() !== MenuState.PartnerAdmin && appLauncherAccount && appLauncherAccount.accountId !== this.claims.accountId) { 
							this.menuSvc.isSwitcherProcessingRequired = false;
							this.appLauncherService.setAccount(undefined);
							this.menuSvc.initialRoute = [events.url === '/accounts' ? 'accounts' : ''];
							this.menuSvc.setState(MenuState.PartnerAdmin);
						}

						if ( this.appUserClaimsSvc.isAssumedRole () )
						{
							await this.authService.revertRole();
						}
						await this.updateAvailablityBasedOnRoleChange();
						console.log('Function: FROM ROUTER, events.url: ', events.url );
						// Following code is not being used but right now, but let it stay here for now to see
						// if something is broken without it. If nothing then we will remove it in future.

						// let claim =  this.appUserClaimsSvc.getClaims();							 
						// if (this.menuSvc.getMenuState() !== MenuState.PartnerAdmin && claim) {  			
						// 	console.log('Function: SET MENU TO PARTNER FROM ROUTER, : ', claim.accountId);	
						// 	this.menuSvc.initialRoute = [events.url === '/dashboard' ? 'dashboard' : events.url === '/accounts' ? 'accounts' : ''];
						// 	this.menuSvc.setState(MenuState.PartnerAdmin);
						// 	console.log("ACCOUNTS APP changing company ", claim.accountId);
						// 	this.companySwitcherService.setCurrentAccountById(claim.accountId);
						// }
					}
				});
			} else if (events instanceof RoutesRecognized && events.url !== events.urlAfterRedirects) {
				sessionStorage.removeItem('currentUrl');	// To redirect to default url returned by the side nav when fake url is typed in the 
			}													// url field. This is specifically to make redirect to dashboard work when user logged in is 
		}));													// account admin or assumed role.	

    this.subscription.add(this.accService.currentAccountChanged.subscribe(async (account: Account) => {
      this.alerts = [];
      this.mivbAccountSettingsService.resetMiVBAccountLevelSettings();
      this.asyncJobsService.clearAsyncJobs();
      await this.determineAlertsEnabledByFeature(account);
      this.setAsyncJobs();
      // hasZappa || hasUnifyPhone
      if (this.enableAlertsTenant) {
        this.clNotifyService.initializeNotifyForBillingAsync();
      }
	}));
    this.subscription.add(this.asyncJobsService.asyncJobsChanged.subscribe(async (asyncJobs: AsyncJobData[]) => {
	  // checking to see if the user can see the async jobs. If not there might have been a change that allows it
	  // so lets check if it should be enabled (scenario: integration added and immediately perform async op)
	  if ((!this.enableAlertFeature) && this.enableAlertsUser) {
		let currAccount = this.accService.getCurrentAccount();
		if (currAccount)
			await this.determineAlertsEnabledByFeature(currAccount);
	  }
      if(this.enableAlertFeature && this.enableAlertsUser){
        let newAlerts = [];
        asyncJobs.map(asyncJob => { 
          let alert = this.alerts.find(aj => aj.id === asyncJob.asyncJobId);
          if(!alert){
            newAlerts.push(this.createAlertFromAsyncJob(asyncJob));
          } else {
            alert.type = asyncJob.type;
            alert.message = asyncJob.message;
            alert.dismissible = asyncJob.dismissible;
            alert.createdOn = asyncJob.createdOn;
            alert.modifiedOn = asyncJob.modifiedOn;
            alert.btnCloseText = asyncJob.btnCloseText;
            alert.details = asyncJob.details;
						alert.link = asyncJob.link;
            alert.linkMessage = asyncJob.linkMessage;
          }
        });
        if(newAlerts.length){
          newAlerts = newAlerts.concat(this.alerts);
          this.alerts = newAlerts.sort((a,b) => (a.createdOn > b.createdOn) ? -1 : 1 );
        }
      }
		}));
		this.subscription.add( this.translateSvc.onLangChange.subscribe( event => {
			console.log("The language has changed", this.translateSvc.currentLang);
			if ( this.translateSvc.currentLang !== this.currentLanguage )
			{
				//to prevent unnecessary messages, only notify on change
				this.currentLanguage = this.translateSvc.currentLang;
				this.childCommsService.notifyChildrenOfLanguageChange(this.currentLanguage);
				this.translateMenus();
				this.walkMeService.pushLanguageChangeToWalkMe( this.currentLanguage );
				document.documentElement.setAttribute( "lang", this.currentLanguage ); // to update the <html lang> attribute

        //notify async jobs        
				this.asyncJobsService.notifyLanguageChange(this.currentLanguage);
			}
		}));
		this.subscription.add(this.orderService.partnerBillingAccountStatusChanged.subscribe(state => {
			// a billing account has been created for the partner when there was not one before
			this.menuSvc.setBillingAccountState(state);
		}));		
	}

	ngOnDestroy() {
		this.usersService.destroySubscriptions();
		this.accService.destroySubscriptions();
		this.orderService.destroySubscriptions();
		this.mivbAccountSettingsService.destroySubscriptions();
		if ( this.subscription )
			this.subscription.unsubscribe();
		this.onDestroy$.next();
	}

	// checks for a new load of the Accounts app
	checkForUpdates()
	{
		// check for updates regularly since our app can stay open for a long time
		// without reload
		// consider extending the interval to something longer.
		setInterval(() => {
			this.pwaService.checkForUpdate();
		}, 300000);
		this.subscription.add(this.pwaService.updateAvailable$.subscribe(update => {
			this.showUpdateBanner = update;
		}));
	}

  createAlertFromAsyncJob(asyncJob: AsyncJobData): AlertData {
    let alert: AlertData = {
      id: asyncJob.asyncJobId,
      type: asyncJob.type,
      message: asyncJob.message,
      dismissible: asyncJob.dismissible,
      createdOn: asyncJob.createdOn,
      modifiedOn: asyncJob.modifiedOn,
      btnCloseText: asyncJob.btnCloseText,
      details: asyncJob.details,
      link: asyncJob.link,
      linkMessage: asyncJob.linkMessage
    };
    return alert;
  }

  onAlertUpdated(alertUpdate: {alert: AlertData, update: string }) {
    this.asyncJobsService.updateAlert(alertUpdate)
  }

	/* istanbul ignore next */ 
	// ignoring during testing because we dont want a reload to happen
	// reload the window to get the lastest load
	updateLoad()
	{
		window.location.reload();
	}

	getCompanyUrls() {
		if ( this.xlaunchService.isXLaunchedFromBoss() )
		{
			return {
				support: this.translateSvc.instant('companyUrl.xlaunchSupport'),
				legal: this.translateSvc.instant('companyUrl.legal')
			};
		}
		return {
			support: this.translateSvc.instant('companyUrl.support'),
			legal: this.translateSvc.instant('companyUrl.legal')
		};
	}

	async handleUserClaims(claims: UserClaims) {
		console.log('in handle user claims ', claims);
		this.startupComplete.push(this.waitForClaimsHandled());
		// This app operates on the original user claims emitted from the toolbar
		// eventhough the app might assume role. There is an issue on refresh(or starting from existing token) of the
		// application because the app will get the claims for the assumed role and not the original role
		// This means some scenarios will not work
		// The method below will determine if there is an original token and use that instead so the app works.
		// Long term depending on the direction of this app, this might not be the correct way to do this. An 
		// alternative would be for the app to update its claims for every revert/assume role and notify the toolbar to do the same.
		// this would be a significant change and I don't know if it is warranted at this point.
		
		this.enableAlertsUser = claims.role === UserRoles.ACCOUNT_ADMIN || claims.role === UserRoles.PARTNER_ADMIN;
		this.setAsyncJobs();

		if(this.appStateService.getSupportPortalMode()){
			this.handleUserClaimsForSupportPortal(claims);
			this.walkMeService.setUserRole( "GLOBAL_ADMIN", false );
			return;
		}

		if(claims.role === 'GLOBAL_ADMIN'){
			this.walkMeService.setUserRole( "GLOBAL_ADMIN", false );
			if(this.appStateService.getFeatureFlagsMode()){
				this.handleUserClaimsForFeatureFlags(claims);
				return;
			} else {
					this.handleUserClaimsForSupportPortal(claims);
					return;
				}
		}

		// the claims don't give us all the information we need so we need the user info too
		await this.getLoggedInUserInfo(claims); 

		// the url needs to be handled first so that the external requests are handled properly before the 
		// claims are handled and possibly altering the token
		this.waitForURLHandled().then( async() => {

			let accountIdFromSessionUrl = this.getAccountIdFromSessionUrl();
			if ( accountIdFromSessionUrl )
			{
				await this.determineAlertsEnabledByFeature({accountId: accountIdFromSessionUrl}); // requires an account not an account id
				this.setAsyncJobs();
			}

			if ( this.appUserClaimsSvc.isAssumedRole() && claims.role === UserRoles.ACCOUNT_ADMIN){
				let newClaims = await this.shouldClaimsChangeAfterRefresh();
				if ( newClaims != null ) {
					this.claims = newClaims;
					this.menuSvc.isSwitcherProcessingRequired = true;

					//If we don't have an accountIdFromSessionUrl - then we can't know which account we are assumed into.
					//It seems the user updates the url from account route to partner route
					if(!accountIdFromSessionUrl){
						if(this.claims.role === UserRoles.PARTNER_ADMIN){
							await this.handleGoHome();
						}
					}
				}
				else
					this.claims = claims;
		
				console.log("HANDLE USER CLAIMS, sent and changed to", claims, newClaims);
			} else {
				this.claims = claims;
				//If we have an accountIdFromSessionUrl as PARTNER_ADMIN, we cannot switch to assumed role as we don't have the original token. 
				//We should update the current url to clear the 'assumed role state', and go to dashboard.
				//It seems the user updated the url from partner route to account route
				if( !this.appUserClaimsSvc.isAssumedRole() && this.claims.role === UserRoles.PARTNER_ADMIN && accountIdFromSessionUrl){
					console.log("Set menu to Partner Admin - go to dashboard");
					sessionStorage.setItem('currentUrl', '/');
					this.menuSvc.initialRoute = [];
					this.menuSvc.setState(MenuState.PartnerAdmin);
				}
			}
			if (this.claims.role === UserRoles.ACCOUNT_ADMIN) {
				console.log('Function: handleUserClaims, handleUserClaims: ', this.menuSvc.initialRoute);
				let isChangeRequired = this.menuSvc.initialRoute.findIndex(item => item === this.claims.accountId) < 0;
				if (isChangeRequired) {
					this.menuSvc.initialRoute = sessionStorage.getItem('currentUrl') ? [] : ['dashboard', this.claims.accountId];
					this.menuSvc.setState(MenuState.AccountAdmin, this.claims.accountId);
				}
			} else if( this.appUserClaimsSvc.isAssumedRole() && this.claims.role === UserRoles.PARTNER_ADMIN) {
				/*
				This case occurs if you are a Partner_Admin who is assumed role in Accounts app, and then change the url so you are 
				redirected back to Accounts app from the Gateway Portal (e.g ip address of gateway appliance). (Need to have policy).
				When you are redirected from Gateway Portal to Accounts app, you will have Partner Claims, and the original token still
				remains in the local storage (assumed). The currentlUrl still remains in session storage, so we can determine which account 
				the user was looking at before the redirect. We will assume role again into that account in HandleCompanies.

				Also we have the case where you launch Accounts app from Miaccess. Then assume role into a particular account. Then launch
				Accounts app again from MiAccess. In this case, the sessionUrl gets cleared so we don't know which account was previously
				selected.
				*/
				console.log('handleUserClaims, assumed partner admin - initialRoute: ', this.menuSvc.initialRoute);
				
				//If we don't have an accountIdFromSessionUrl - then we can't know which account we are assumed into. 
				//We should remove the original token to clear the assumed role state, and go to dashboard.
				if(!accountIdFromSessionUrl){
					console.log("Assumed Partner Admin with no session accountId");
					this.removeOriginalToken();
					this.menuSvc.initialRoute = [];
					this.menuSvc.setState(MenuState.PartnerAdmin);
				} else{
					console.log("Assumed Partner Admin with session accountId");
					this.menuSvc.initialRoute = sessionStorage.getItem('currentUrl') ? [] : ['dashboard', accountIdFromSessionUrl];
					this.menuSvc.setState(MenuState.AccountAdmin, accountIdFromSessionUrl);
				}
			}
			console.log("The current initial route is 1 ", this.menuSvc.initialRoute);
			this.appUserClaimsSvc.setClaims(this.claims);
			this.accService.getPrimeAccountInformation();
			this.usersService.getAllPartnerUsers();

			// the earliest I can determine
			let hasAccount = await this.orderService.hasABillingAccount();
			this.menuSvc.setBillingAccountState(hasAccount);
			if (this.claims.role === UserRoles.ACCOUNT_ADMIN && !this.appUserClaimsSvc.isAssumedRole())
			{
				let isPrimeAccount = await this.accService.isPartnerPrimeAccount();
				if ( isPrimeAccount == false )
					this.menuSvc.setBillingAccountState(true); // there is no way to know if a partner has a billing account
			}
			await this.testForPolicies(this.claims);
			this.walkMeService.setUserRole( this.claims.role, this.appUserClaimsSvc.isAssumedRole() );

			if (this.claims.role === UserRoles.PARTNER_ADMIN && !this.appUserClaimsSvc.isAssumedRole()) {
				/*
				We only want to set the menu as Partner if they are not assumed role.
				If they are assumed role then we want to set the menu as Account Admin.
				That case would happen if a partner is assumed role in Accounts App and then is redirected 
				from Gateway Portal back into Accounts app)
				*/
				console.log("this is a partner admin so set the menus accordingly");
				this.menuSvc.setState(MenuState.PartnerAdmin);
			} else if(this.claims.role != UserRoles.PARTNER_ADMIN){
				this.menuSvc.setState(this.menuSvc.getMenuState(), claims.accountId);
			}
			this.appUserClaimsSvc.setClaimsHandled(true); //helpful for refreshing when we don't want to do load anything unless this method completes
			console.log("The current initial route is 2 ", this.menuSvc.initialRoute);
			console.log("MenuState after handleUserClaims: ", this.menuSvc.getMenuState());
			//read RBAC permissions
			let user = await this.getClaimsUserWithTags();
			await this.rbacAuthGuardService.setRBACPermissionsByUser(user);
			this.menuSvc.setState(this.menuSvc.getMenuState(), claims.accountId);
		})
	}

	async handleUserClaimsForSupportPortal(claims: UserClaims){
		this.claims = claims;
		this.appUserClaimsSvc.setClaims(this.claims);
		if(this.appStateService.getSupportPortalMode() && claims.role === "GLOBAL_ADMIN"){
			this.appUserClaimsSvc.setCloudFromClaims();
			this.menuSvc.initialRoute = ['support-search'];
			this.menuSvc.setState(MenuState.GlobalAdmin);
			let user = await this.getClaimsUserWithTags();
			this.supportPortalService.setupSupportPortalPermissions(user);
			this.rbacAuthGuardService.setSupportPortalPermissionsByUser();
		}
		this.appUserClaimsSvc.setClaimsHandled(true);
	}

	async handleUserClaimsForFeatureFlags(claims: UserClaims){
		this.claims = claims;
		this.appUserClaimsSvc.setClaims(this.claims);
		if(this.appStateService.getFeatureFlagsMode() && claims.role === UserRoles.GLOBAL_ADMIN){
			this.appUserClaimsSvc.setCloudFromClaims();
			this.menuSvc.initialRoute = ['feature-flags'];
			this.menuSvc.setState(MenuState.FeatureFlagsAdmin);
			this.featureFlagPolicyService.setupFeatureFlagPermissions(claims);
		}
		this.appUserClaimsSvc.setClaimsHandled(true);
	}

	async getClaimsUserWithTags(){
		let claims: any = this.claims; //any since UserClaims interface does not include userId
		let user: any = undefined;
		if(claims && claims.userId){
			try {
				user = await this.adminService.getUser(claims.userId, 'tags');
			} catch (err) {
				console.log("Could not get claims user");
			}
		}
		console.log("Claims User: ", user);
		return user;
	}

	async getLoggedInUserInfo(claims)
	{
		let user: any = undefined;
		if ( claims && claims.userId && claims.providerAccountId )
		{
			try
			{
				user = await this.adminService.getUserForAccountNoFrills( claims.providerAccountId, claims.userId );
				this.appUserClaimsSvc.setLoggedInUser(user);
			}
			catch(err)
			{
				console.log('There was an error getting the user', claims.userId, claims.providerAccountId, err)
			}
		}
		else if ( claims && claims.userId && claims.accountId )
		{
			try
			{
				user = await this.adminService.getUserForAccountNoFrills(claims.accountId, claims.userId );
				this.appUserClaimsSvc.setLoggedInUser(user);
			}
			catch(err)
			{
				console.log('There was an error getting the user', claims.userId, claims.accountId, err)
			}
		}
		else if ( claims && claims.userId ) // last resort
		{
			try
			{
				user = await this.adminService.getUser(claims.userId);
				this.appUserClaimsSvc.setLoggedInUser(user);
			}
			catch(err)
			{
				console.log('There was an error getting the user', claims.userId, claims.accountId, err)
			}
		}
		console.log("this is the logged in user info", user);
	}

	waitForClaimsHandled():Promise<void>
	{
		return new Promise( (resolve, reject) => {
			this.subscription.add(this.appUserClaimsSvc.claimsHandled.subscribe( flag =>
				{
					resolve();
				}));
		})
		
	}

	waitForURLHandled():Promise<void>
	{
		return new Promise( (resolve, reject) => {
			this.subscription.add(this.extEventsHandlerService.urlProcessComplete.subscribe( flag =>
			{
				// only resolve once the external url request is handled
				if ( this.extEventsHandlerService.urlProcessComplete.value == true )
					resolve();
			}));
		})
	}

	async testForPolicies(claims: UserClaims)
	{
		console.log("$$$CHECKING FOR FEATURE POLICIES");
		let assumeRole = false;
		if ( this.appUserClaimsSvc.isAssumedRole() )
		{
			await this.authService.revertRole();
			assumeRole = true;
		}
		await this.setupGWPIntegration();
		await this.featureToggleService.initAllFeatureFlags( claims );
		this.menuSvc.setAccountDelegationAccess(
			await this.accountManagementService.showAccountManagersMenuItem()
		);
		this.menuSvc.setManagedAccountsAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MANAGED_ACCOUNTS));
		this.menuSvc.setAPIDeveloperAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_DEVELOPER_API));
		this.menuSvc.setPartnerUserTemplateAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_PARTNER_TEMPLATES));
		this.menuSvc.setSystemInventoryAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_SYSTEM_INVENTORY));
		this.menuSvc.setSubPartnerState(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_SUB_PARTNER));
		this.menuSvc.setManagementProfilesAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MANAGEMENT_PROFILES));
		if ( assumeRole )
		{
			await this.authService.assumeRole("ACCOUNT_ADMIN", claims.accountId);
		}
		console.log("$$$DONE CHECKING FOR FEATURE POLICIES");
	}

	async updateAvailablityBasedOnRoleChange()
	{
		let currentClaims = await this.authService.getCurrentClaims();
		await this.featureToggleService.updateFeatureFlagsOnRoleChange(currentClaims as UserClaims);
		// there are certain areas that need to be updated
		// TBD In the future refactor and fire an event that can be captured by the menu service? Needs more analysis
		this.menuSvc.setSystemInventoryAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_SYSTEM_INVENTORY));
		this.menuSvc.setManagementProfilesAccess(this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MANAGEMENT_PROFILES));
		this.walkMeService.setUserRole( currentClaims.role, this.appUserClaimsSvc.isAssumedRole() );
	}

	shouldClaimsChangeAfterRefresh(): Promise<UserClaims>
	{
		if ( this.appUserClaimsSvc.isAssumedRole() )
		{
			return this.authService.getPartnerClaims().then(claims => claims as UserClaims);
		}
		else
			return Promise.resolve(null);
	}

	onMenuItemClicked(url: string) {
    console.log('menu item clicked url', url);
		if ( url !== this.DO_NOT_ROUTE &&  !this.appStateService.getBlockNavigation())
		{
			if ( sessionStorage.getItem('refreshed') === 'true') {
				sessionStorage.setItem('refreshed', 'false');
				url = sessionStorage.getItem('currentUrl');
			}
			if(url === '/support-search'){
				console.log("let's go to handleGoToSupportSearchPage");
				this.handleGoToSupportSearchPage();
			} else if (url === '/report-a-problem') {
				this.popReportProblemModal();
			}
			else{
				const menuItemId = this.sideNav.getItemIdByUrl(url);
				let acct = this.accService.getCurrentAccount();
				let clickedItem = undefined;
				if ( acct )
					clickedItem = this.menuSvc.getMenuItemById( acct.accountId, menuItemId );
				// kludge for CX Help - open url in another window but
				// do not change the active menu in the side nav
				if ( acct && clickedItem && clickedItem.openInNewWindow == true )
				{
					window.open(url, menuItemId);
					this.sideNav.defaultItemId = this.selectedMenuItemId;
				}
				else
				{
					// proceed as normal
					sessionStorage.setItem('currentUrl', url);
					this.selectedMenuItemId = menuItemId;
					if(clickedItem && clickedItem.routeData){
						let navExtras: NavigationExtras = {
							state: clickedItem.routeData
						};
						this.router.navigateByUrl(url, navExtras);
					}
					else{
						this.router.navigateByUrl(url);
					}
		
				}

			}
			// This code can execute only when url field is changed to '/' when user is assumed role. Right now this can not be executed 
			// at all because of route guard on dashboard page which redirects to access denied page if role is assumed. 
			// This behaviour might change when flow for using back and forward button is decided. As part of that we might have to enable the 
			// user to go to dashboard page by reverting it's role.
			// If this code is removed then also remove the test for this code which is skipped right now.
			//  .then(() => {
			// 	if (url === '/' && this.menuSvc.getMenuState() !== MenuState.PartnerAdmin && this.appUserClaimsSvc.isAssumedRole() && !!sessionStorage.getItem('refreshed')) {
			// 		console.log("Inside onMenuItemClicked after navigate***********");
			// 		this.menuSvc.initialRoute = [''];
			// 		this.menuSvc.setState(MenuState.PartnerAdmin);
			// 	}
			// });
		}
	}

	async handleGoToSupportSearchPage() {
		console.log("handleGoToSupportSearchPage");
		console.log("claims", this.claims);
		if (this.claims.role === UserRoles.GLOBAL_ADMIN) {
			let launcherAccount = this.appLauncherService.getAccount();
			console.log("laucher account: ", launcherAccount);
			if((launcherAccount && this.claims.accountId != launcherAccount.accountId) || this.appUserClaimsSvc.isAssumedRole()) {
				this.menuSvc.isSwitcherProcessingRequired = false;
				this.menuSvc.initialRoute = ['support-search'];
				this.appLauncherService.setAccount(undefined);
				await this.authService.revertRole();
				this.menuSvc.setState(MenuState.GlobalAdmin);
				/* Does not seem necessary at this point but may want to also 
				set current account to the global account - like in handle companies global
				*/
			}
			this.router.navigate(['support-search'])
				.catch((err) => console.error('Navigation to Support-Search ERROR: ', err ));
		}	
	}

	async handleGoToFeatureFlagsPage() {
		console.log('handleGoToFeatureFlagsPage');
		if (this.claims.role === UserRoles.GLOBAL_ADMIN) {
			this.router.navigate(['feature-flags'])
				.catch((err) => console.error('Navigation to feature-flags ERROR: ', err ));
		}	
	}

	getAccountIdFromSessionUrl() {
		let url = sessionStorage.getItem('currentUrl');
		let urlparams = null;
		if (url && url.includes("/accounts/"))
		{
			urlparams = url.split("/accounts/");
		} else if(url && url.includes("/dashboard/")){
			urlparams = url.split("/dashboard/");
		}
		if(urlparams && urlparams.length >=2){
			let accountId = urlparams[1].split("/", 1).toString();
			return accountId;
		}
		return null;
	}

	async handleCompanies(account: Account) {

		Logging.clearLogs();
		/*
		If you are a Parnter_Admin who is assumed role, when you do a refresh, the account will be the customer account.
		If you are a Partner_Admin who is assumed role, and change the url so you are redirected back to Accounts app
		from the Gateway Portal (e.g ip address of gateway appliance), then the account will be the prime account, but 
		there will still be an original token.
		*/
		console.log("handleCompanies: change in company to --> ", account.name);
		console.log("handleCompanies: this.appUserClaimsSvc.isAssumedRole() --> " + this.appUserClaimsSvc.isAssumedRole());
		console.log("handleCompanies: this.menuSvc.getMenuState() --> " + this.menuSvc.getMenuState());
		console.log("handleCompanies: this.menuSvc.isSwitcherProcessingRequired --> " + this.menuSvc.isSwitcherProcessingRequired);
		console.log("handleCompanies: Initial route value", this.menuSvc.initialRoute);
		
		// is everything initialized before we start changing the token?
		Promise.all(this.startupComplete).then( async result => 
		{
			console.log("handleCompanies: startup complete");

			this.orderService.clearAcctSubscriptionsCache();
			this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache();
			this.clNotifyService.unsubscribeFromNotifications();

			if(this.claims && this.claims.role && this.claims.role === UserRoles.GLOBAL_ADMIN)
			{
				this.handleCompanies_Global(account);
				return;
			}
			
			// we will determine if this account is for CONNECT and redirect to access denied if so
			this.redirectIfNotCloudlinkAccount(account);

			// is this the correct place for this?
			if ( this.xlaunchService.isValidConnectLogin() )
			{
				console.log("It is a valid CONNECT login, redirect")
				await this.redirectValidConnectLogin( account );
				return;
			} else {
				this.clientDropMenu = this.getDropMenuConfig();
			}

			if ((this.appUserClaimsSvc.isAssumedRole() && this.menuSvc.getMenuState() !== MenuState.PartnerAdmin) || this.menuSvc.isSwitcherProcessingRequired) {

				if ( this.extEventsHandlerService.urlProcessComplete.value == false )
						return;

				//if I am assumed role, and the accountId does not match the accountID in the session URL,
				//then I will need to get that account instead
				let changeAssumeRoleAccount = false;
				let accountIdFromSessionUrl = null;
				if(this.appUserClaimsSvc.isAssumedRole() && this.firstTime){
					accountIdFromSessionUrl = this.getAccountIdFromSessionUrl();
					if(accountIdFromSessionUrl && (account.accountId !== accountIdFromSessionUrl)){
						// we know we will need to switch accounts
						// the company switcher and the session have a different accountID 
						changeAssumeRoleAccount = true;
					}
				}

				//If it's the GWP Partner claims going to a customer account
				//handleCompanies will be called twice. 
				// 1. On initial load with the Prime account
				// 2. At the end of the first handleClaims we set the company in the toolbar to match the customer - triggers handleClaims again
				// In case 2, we don't need to revert and assume again. We just did that the first time through  
				
				if(!this.skipRevert) 
				{
					console.log("Reverting Role");
					await this.authService.revertRole();
	
					
					if(changeAssumeRoleAccount){
						try{
							account = await this.adminService.getAccount(accountIdFromSessionUrl);
						}
						catch(err){
							console.error(`ERROR retrieving account from accountIdFromSessionUrl: ${accountIdFromSessionUrl}`);
							changeAssumeRoleAccount = false;
							this.skipRevert = false;
						}
					}
					
					console.log("Assuming role for account id", account.accountId);
					await this.authService.assumeRole( 'ACCOUNT_ADMIN', account.accountId);
					await this.updateAvailablityBasedOnRoleChange();
				}
				this.skipRevert = false;
				
				if (this.menuSvc.initialRoute && this.menuSvc.initialRoute.length > 0) {
					let actualRoute = this.router.url.split('/').slice(1);

					// sometimes the actual url is out of sync with the initial route
					// in this case update the initial route
					if ( actualRoute.length >= this.menuSvc.initialRoute.length )
						this.menuSvc.initialRoute = actualRoute;

					let route = this.menuSvc.initialRoute;
					// this is potentially a switch of accounts so figure out the current menu id
					// so later we can determine if it is still valid for the account
					let currentMenuId = ((this.menuItems && this.menuItems.length > 0 ) ? this.sideNav.getItemIdByUrl(this.router.url) : undefined);

					if (this.menuSvc.initialRoute.length > 1 && this.menuSvc.initialRoute[1] !== account.accountId) {
						route[1] = account.accountId;
						this.menuSvc.initialRoute = route;
					}
					this.menuSvc.clearFeatureSpecificMenusForAccount();

					await this.accService.setCurrentAccountById(account.accountId).catch(err => console.log('Function: setCurrentAccountById, err: ', err));

					let primeAcct = await this.accService.isPartnerPrimeAccount();
					this.menuSvc.setIsPartnerPrimeAccount(primeAcct);
					this.menuSvc.checkForFeatureSpecificMenusForAccount(account).then(menusFound => {
						// once we get here, lets see if the menu exists for the account
						// it might not exist because of feature flags
						// this if below points to a company switcher scenario
						if (this.router.url &&
							this.router.url.startsWith('/accounts/') && // if the url is not a customer account url, then this case is not valid
						 	!this.router.url.startsWith('/accounts/' + account.accountId)) { // change in account	

							// does this menu still exist in the new account?
							let menuData = this.menuSvc.getMenuItemById(this.menuSvc.initialRoute[1], currentMenuId);
							if ( menuData )
								this.menuSvc.initialRoute = menuData.navigation.split('/').slice(1); // use the menu url not the original one

							if ( menuData && (menuData.isFederatedSPA == true) )
								// yup, so tell the child Fed module to switch companies
								this.childCommsService.sendChildSwitchedAccountMessage(account.accountId);
							else if ( menuData == undefined )
							{
								// new company does not have this menu item so redirect to accounts edit page
								this.menuSvc.initialRoute = ['accounts', account.accountId];
							}
						}
						this.menuSvc.setState(MenuState.AccountAdmin, account.accountId, this.menuSvc.initialRoute);
					});
				}
				if ( sessionStorage.getItem('refreshed') === 'false') {
					await this.accService.setCurrentAccountById(account.accountId).catch(err => console.log('Function: setCurrentAccountById, err: ', err));
					this.menuSvc.clearFeatureSpecificMenusForAccount();
					let primeAcct = await this.accService.isPartnerPrimeAccount();
					this.menuSvc.setIsPartnerPrimeAccount(primeAcct);
					this.menuSvc.setState(MenuState.AccountAdmin, account.accountId, this.menuSvc.initialRoute);
					if ( this.appUserClaimsSvc.getClaimsHandledFlag() == true ) 
					{  
						this.menuSvc.checkForFeatureSpecificMenusForAccount(account).then(menusFound => {
							this.menuSvc.setState(MenuState.AccountAdmin, account.accountId);
						});
					}
					else
					{
						// we need to wait
						 this.appUserClaimsSvc.claimsHandled.subscribe( flag => {
							this.menuSvc.checkForFeatureSpecificMenusForAccount(account).then(menusFound => {
								this.menuSvc.setState(MenuState.AccountAdmin, account.accountId);
							});
						});
					}
				}

				this.menuSvc.isSwitcherProcessingRequired = false;
				if(changeAssumeRoleAccount){
					this.skipRevert = true;
					this.companySwitcherService.setCurrentAccount(account);
				}
			}
	
		else if ( this.claims && this.claims.role && 
			!this.appUserClaimsSvc.isAssumedRole() && this.claims.role === UserRoles.ACCOUNT_ADMIN )
		{
			await this.accService.setCurrentAccountById(account.accountId).catch(err => console.log('Function: setCurrentAccountById, err: ', err));
			this.menuSvc.clearFeatureSpecificMenusForAccount()
			let primeAcct = await this.accService.isPartnerPrimeAccount();
			this.menuSvc.setIsPartnerPrimeAccount(primeAcct);
			this.menuSvc.checkForFeatureSpecificMenusForAccount(account).then(menusFound => {
				this.menuSvc.setState(MenuState.AccountAdmin, account.accountId);
			});
		}

			sessionStorage.removeItem('refreshed');
			// Need to change the account in app launcher service so that query params can be attached for the proper account
			this.appLauncherService.setAccount(account);

			// Are the claims ever updated after the assume role?
			if (this.claims && this.claims.role &&
				(this.claims.role === UserRoles.PARTNER_ADMIN ||
					this.claims.role === UserRoles.ACCOUNT_ADMIN)) {
				this.accService.getAccountTagsById(account.accountId).then(tags => {
					//update tags when assuming role 
					this.accService.setAccountTag(tags);
				}).catch(err => {
					// eat it
				});
			}
			this.firstTime = false; //made it through handleCompanies the first time
		})
	}
	
	handleCompanies_Global(account: Account){
		/*
		To be further developed when we allow Global_Admins to assume role
		*/
		console.log("In handleCompanies_Global");
		if(this.firstTime){
			/* 
			When the user first logs in, store their partnerId in order to prevent
			them from supporting their own partner accounts 
			*/
			if(this.appStateService.getSupportPortalMode()){
				this.supportPortalService.setUsersSupportPartnerId(account.partnerId);
			}
		}
		if (this.menuSvc.initialRoute && this.menuSvc.initialRoute.length > 0) {
			let route = this.menuSvc.initialRoute;

			if (this.menuSvc.initialRoute.length > 1 && this.menuSvc.initialRoute[1] !== account.accountId) {
				route[1] = account.accountId;
				this.menuSvc.initialRoute = route;
			}
			this.menuSvc.clearFeatureSpecificMenusForAccount();
			this.menuSvc.setBillingAccountState(false);
			this.menuSvc.setManagedAccountsAccess(false);
			this.accService.setCurrentAccount(account);
			this.accService.setAccounts([account]);
			this.orderService.clearAcctSubscriptionsCache();
			this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache();
			this.clNotifyService.unsubscribeFromWorkflow();
			if (this.appStateService.getSupportPortalMode()) {
				this.menuSvc.setState(MenuState.GlobalAdmin, account.accountId, this.menuSvc.initialRoute);
			} else if (this.appStateService.getFeatureFlagsMode()) {
				this.menuSvc.setState(MenuState.FeatureFlagsAdmin, account.accountId, this.menuSvc.initialRoute);
			}
		}
		sessionStorage.removeItem('refreshed');
		this.appLauncherService.setAccount(account);
		this.firstTime = false;
	};

	async handleGoHome() {
		if ( this.checkForInvalidLogin() == true )
			return;
		// in the x-launch scenario, this event is a no-op
		if ( this.xlaunchService.isXLaunchedFromBoss() )
			return;
		/* Only proceed if navigation is not set to be blocked
		e.g Navigation could be blocked if an iframe has unsaved changes */
		if(this.appStateService.getBlockNavigation() === false){
			this.clearAlertFeatureFlags();
			this.enableAlertsUser = this.claims.role === UserRoles.ACCOUNT_ADMIN || this.claims.role === UserRoles.PARTNER_ADMIN;
			if (this.claims.role === UserRoles.ACCOUNT_ADMIN) {
				await this.determineAlertsEnabledByFeature({ accountId: this.claims.accountId } as Account);
				this.setAsyncJobs();
				this.router.navigate(['dashboard', this.claims.accountId])
					.catch((err) => console.error('Function: navigate, ERROR: ', err ));
			} else {
				this.setAsyncJobs();
				if (this.claims.role === UserRoles.PARTNER_ADMIN) {
					this.menuSvc.isSwitcherProcessingRequired = false;
					this.menuSvc.initialRoute = ['accounts'];
					if ( this.appUserClaimsSvc.isAssumedRole() ) {
						this.appLauncherService.setAccount(undefined);
						await this.authService.revertRole();
						await this.updateAvailablityBasedOnRoleChange();
					}
					this.menuSvc.setState(MenuState.PartnerAdmin);
				} else if(this.claims.role === UserRoles.GLOBAL_ADMIN && this.appStateService.getSupportPortalMode()) {
					console.log("Returning to Support Search");
					/* this will be built out more once we allow for Global_Admins to assume role and
					go to other pages */
					this.handleGoToSupportSearchPage();
				} else if(this.claims.role === UserRoles.GLOBAL_ADMIN && this.appStateService.getFeatureFlagsMode()) {
					this.handleGoToFeatureFlagsPage();
				} else { //User role
					this.router.navigate([''])
						.catch((err) => console.error('Function: navigate, ERROR: ', err ));
				}
			}
		}
	}

  setAsyncJobs() {
    if(this.enableAlertsCAP && this.enableAlertsUser){
      this.asyncJobsService.initAsyncJobs(asyncJobOwner.MIVB, asyncJobOwner.USER_TEMPLATES);
    } else {
      this.asyncJobsService.stopAsyncJobs(asyncJobOwner.MIVB);
      this.asyncJobsService.stopAsyncJobs(asyncJobOwner.USER_TEMPLATES);
    }

	if (this.enableAlertsTenant && this.enableAlertsUser) {
		this.asyncJobsService.initAsyncJobs(...this.asyncJobTenantTypes);
	}
  }

  	async determineAlertsEnabledByFeature(account)
	{
		this.enableAlertsCAP = await this.capIntegService.isCAPFeatureEnabled(account);
		const hasZappa = await this.zappaAcctService.doesAccountHaveZappaPhone(account.accountId);
		const hasUnifyPhone = await this.integUnifyPhoneService.isSetupComplete(account);
		this.enableAlertsTenant = hasZappa || hasUnifyPhone;
		this.enableAlertFeature = this.enableAlertsCAP || this.enableAlertsTenant;
		this.asyncJobTenantTypes = [...(hasZappa ? [asyncJobOwner.ZAPPA_PHONE] : []), ...(hasUnifyPhone ? [asyncJobOwner.UNIFY_PHONE] : [])];
	}

	clearAlertFeatureFlags()
	{
		this.enableAlertsCAP = false;
		this.enableAlertsTenant = false;
		this.enableAlertFeature = false;
		this.asyncJobTenantTypes = [];
	}

	setupTranslationService() 
	{
		this.languages = this.appInfoService.getSupportedLanguages();
		const langRegex = new RegExp(
		  this.appInfoService.getLangCodes().join('|'),
		  'gi'
		);
		this.translateSvc.addLangs(this.appInfoService.getLangCodes());
		this.translateSvc.setDefaultLang('en');
		const browserLang = this.translateSvc.getBrowserLang();
		console.log(' TS browser lang: ', browserLang);
		if(browserLang == 'en'){
			const language = this.translateSvc.getBrowserCultureLang();
			this.translateSvc.use(language.match(/en-GB/) ? language : 'en');
		}
		else
			this.translateSvc.use(browserLang.match(langRegex) ? browserLang : 'en');

		this.currentLanguage = this.translateSvc.currentLang;
		this.walkMeService.initWalkMeLanguage( this.currentLanguage );
		document.documentElement.setAttribute( "lang", this.currentLanguage ); // to update the <html lang> attribute
	}

	translateMenus()
	{
		if ( !this.menuItems || this.menuItems.length === 0 )
			return;
		
		let menuItem, child;
		for (menuItem of this.menuItems) {
			const key = 'menus.' + menuItem.translationKey;
			menuItem.name = this.translateSvc.instant(key);
			if (menuItem.children !== undefined && menuItem.children.length > 0) {
				for (child of menuItem.children) {
					const childkey = 'menus.' + child.translationKey;
					child.name = this.translateSvc.instant(childkey);
					if (
						child.id === "cchelpworkgroups" ||
						child.id === "cchelpvoiceassist"
					) {
						child.navigation = this.translateHelpNavigationUrl(child.id);
					}
				}
			}
		}
	}

	translateHelpNavigationUrl(id: string) { //sebhere: depending on which product va vs wg
		switch(id) {
			case "cchelpworkgroups":
				return (
					"https://productdocuments.mitel.com/doc_finder/UG/Apps-Solutions/Mitel-One-Workgroups/" +
					this.currentLanguage.toUpperCase().substring(0, 2) +
					"/Workgroups_Admin_Help/index.html"
        		);
			case "cchelpvoiceassist":
				return (
          	"https://productdocuments.mitel.com/doc_finder/UG/Apps-Solutions/Mitel-One-Voice-Assist/EN/Voice_Assist_Admin_Help/index.html"
        		);
		}
	}

	handleInvokingUrl( url: string ) {
		this.gwpService.getGWPFlag().subscribe(hasAccess => {
			if (hasAccess) {
				this.gwpService.extractTunnelParameters( url );
				this.showGWPRedirectInformationIfApplicable(url);
			}
		});

		console.log("This is the invoking url", url);
		let params = this.clHeader.getInvokedParams();
		this.startupComplete.push( this.extEventsHandlerService.handleQueryParameters( url, params ) );
		if(this.xlaunchService.determineIfAppWasInvokedFromBoss(url)){
			this.rbacAuthGuardService.setCrossLaunchPermissionsByUser();
		}
	}

	removeOriginalToken(){
		console.log("removing lingering assume role status");
		localStorage.removeItem('@mitel/token.original_token');
	}

	async setupGWPIntegration() {
		let show = await this.policyToggleService.requestGWPIntegrationPolicy();
		console.log('GWP Policy says:', show);
		this.gwpService.setGWPFlag(show);
		this.menuSvc.setGWPPolicy(show);
	}

	async showGWPRedirectInformationIfApplicable(url: string){
		if(url && url.includes('?checkToken=true')){
			let user = await this.getClaimsUserWithTags();
			if(user){
				if(!user.tags || !user.tags['gateway-redirect'] || user.tags['gateway-redirect'] !== 'hide'){
					const dialogRef = this.dialog.open(InformationDialogComponent, { data: { mode: InformationDialogs.GATEWAY_REDIRECT } });
					return dialogRef.afterClosed().toPromise().then(hide => {
						if(hide){
							const userTagParams = { 'gateway-redirect': 'hide' }; 
							this.adminService.createUserTagByAccountIdWithoutSpinner(user.accountId, user.userId, userTagParams);
						}
						return hide;
					});
				}
			}
		}
	}



	redirectIfNotCloudlinkAccount( account: Account )
	{
		let userSource = this.appUserClaimsSvc.getUserSource();
		let connectUser = (userSource == undefined ? true : this.xlaunchService.isConnectSource(userSource));
		if ( this.xlaunchService.isConnectSource(account.source) == true &&
			connectUser == true &&
		 	this.xlaunchService.determineIfValidConnectUserLogin(account) == false )
		{
			this.invalidAccountLogin = true;
			this.showSideNav = false;
			this.router.navigate(['navigationError']);
		}
	}

	checkForInvalidLogin()
	{
		if ( this.invalidAccountLogin == true )
		{
			this.showSideNav = false;
			this.router.navigate(['navigationError']);
			return true;
		}
		return false;
	}

	async redirectValidConnectLogin(account: Account)
	{
		if ( this.firstTime == true )
		{
			sessionStorage.removeItem('refreshed');
			this.menuSvc.clearFeatureSpecificMenusForAccount();
			await this.accService.setCurrentAccountById(account.accountId);
			this.companySwitcherService.setCurrentAccount(account);
			this.clientDropMenu = this.xlaunchService.getDropMenuConfigConnect();
			this.menuSvc.checkForFeatureSpecificMenusForAccount(account).then( async menusFound => {
				this.appLauncherService.setAccount(account);
				this.menuSvc.initialRoute = await this.xlaunchService.getDefaultPageDependingOnContext(account);
				this.menuSvc.setState(MenuState.AccountAdmin, account.accountId, this.menuSvc.initialRoute);
				console.log("AFTER SET STATE FOR XLAUNCH", this.isAccountSelected, this.menuSvc.initialRoute);
			});
			this.firstTime = false;
		}
	}

	handleClientMenuClicked( menuItemId: string )
	{
		switch (menuItemId) {
			case 'exit':
				this.xlaunchService.handleMenuClick(menuItemId);
				break;
			case 'report-problem':
				this.popReportProblemModal();
				break;
			default:
				console.error(`${logContext}.handleClientMenuClicked unexpected menuItemId=${menuItemId}`);
		}
	}

	popReportProblemModal() {
		this.dialog.open(ReportIssueModalComponent, {
			width: '35rem',
			// enterAnimationDuration: '250ms',
			// exitAnimationDuration: '250ms',
			disableClose: false,
			autoFocus: false, // false bc we are controlling the focus inside ReportIssueModalComponent
		  });
	}

	/**
	 * Client menu. (Under the header bar right-side Avatar)
	 * @returns client menu configuration obj.
	 */
	getDropMenuConfig()
	{
		let dropMenu: any = {
			useLogoutMenu: true,   // set to false if you do not want the standard Logout menu
			clientMenus: [
			  {
				id: 'report-problem',
				displayLabelKey: "menus.ReportProblem"
			  }
				
			]
		};
		return dropMenu;
	}

	async checkForClaimsChange(): Promise<void>
	{
		// temporarily turned off while we integrate with cross launch
		if ( this.sessionExpiredDialogShown == true )
			// only show one
			return;

		let acct = this.accService.getCurrentAccount();
		let newClaims = await this.authService.getCurrentClaims();
		let changed = this.appUserClaimsSvc.hasTheClaimsChanged(acct, newClaims);		
		if ( changed == true )
		{
			// show a dialog telling the user that their session has expired
			if ( this.xlaunchService.isXLaunchedFromBoss() )
			{
				this.sessionExpiredDialogShown = true;
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.SESSION_EXPIRED_CLOSE } });
				return dialogRef.afterClosed().toPromise().then(result => {
					this.sessionExpiredDialogShown = false;
					this.xlaunchService.handleMenuClick("exit");
					return result;
				});
			}
			else
			{
				Promise.resolve();
			// 	const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.SESSION_EXPIRED_LOGOUT } });
			// 	return dialogRef.afterClosed().toPromise().then(result => {
			// 		this.clHeader.redirectToLogin();
			// 		return result;
			// 	});
			}
		}
	}

	/**
	 * Controls UX properites that determine if launcher is shown, and which apps are shown.
	 * App Launcher is that little grid on the top header bar (left of users name) that can be
	 * used to launch another app to a new browser tab.
	 */
	handleAppLauncher() {
		combineLatest([
			this.appLauncherService.getAppsUpdated(),
			this.router.events.pipe(filter(event => event instanceof NavigationStart))
		])
			.pipe(
				takeUntil(this.onDestroy$)
			).subscribe(
				([apps, navStart]: [LauncherAppContainer[], NavigationStart]) => {
					this.apps = apps;
					// Show app launcher if there are apps 
					// and we are *not* on /support-search page ( see https://github.com/mitel-networks/cloudlink-console-accounts/pull/1322 )
					this.enableAppLauncher = apps.length > 0
						&& !navStart.url.includes('support-search')
				}
			);
	}
}

